import { apiClient, ApiRequestOptions } from "../../../../utils/apiClient";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../StorageHelper";

const BASE = NGROK;

function enc(v: string): string {
  return encodeURIComponent(v);
}

function gw<T = any>(opts: ApiRequestOptions): Promise<T> {
  const raw = myLocalStorage.getItem("latestTenant");
  const tenant = typeof raw === "string" ? raw : raw?.tenantName || "";
  return apiClient<T>({
    ...opts,
    headers: {
      ...opts.headers,
      "X-WS-Tenant": tenant,
    },
  });
}

export const GatewayDashboardApi = {
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/summary` }),
  health: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/health` }),
  inFlight: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/in-flight` }),
  pdp: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/pdp` }),
};

function mapServerConfig(s: any) {
  return {
    ...s,
    name: s.serverName || s.name,
    transportType: s.type || s.transportType || "STREAMABLE_HTTP",
  };
}

export const GatewayServerConfigApi = {
  list: () =>
    gw<any[]>({ method: "GET", url: `${BASE}/api/admin/mcp-servers` }).then(
      (arr) => (Array.isArray(arr) ? arr.map(mapServerConfig) : [])
    ),
  get: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/mcp-servers/${enc(name)}` }).then(mapServerConfig),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers`, data: body }),
  update: (name: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/mcp-servers/${enc(name)}`, data: body }),
  remove: (name: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/mcp-servers/${enc(name)}` }),
  connect: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/connect` }),
  reconnect: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/reconnect` }),
  test: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/test`, data: body }),
  testSaved: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/test` }),
  enable: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/enable` }),
  disable: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/disable` }),
};

export const GatewayAuthConfigApi = {
  get: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/auth-config` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config`, data: body }),
  update: (body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/auth-config`, data: body }),
  remove: () =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/auth-config` }),
  validate: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/validate`, data: body }),
  discover: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/discover`, data: body }),
  refreshJwks: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/refresh-jwks` }),
};

export const GatewayServersApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers` }),
  health: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/health` }),
  info: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/info` }),
  tools: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/tools` }),
  resources: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/resources` }),
  readResource: (name: string, uri: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/resources/read?uri=${enc(uri)}` }),
  prompts: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/prompts` }),
  callTool: (name: string, tool: string, input: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/mcp/servers/${enc(name)}/tools/${enc(tool)}`, data: input }),
  disconnectClient: (name: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/mcp/servers/${enc(name)}` }),
  disconnectAllClients: () =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/mcp/servers` }),
};

function buildQS(params?: Record<string, any>): string {
  const qs = new URLSearchParams();
  if (!params) return "";
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") return;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== null && item !== undefined && item !== "") qs.append(k, String(item));
      });
    } else {
      qs.append(k, String(v));
    }
  });
  return qs.toString();
}

export const GatewayAuditApi = {
  logs: (params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs?${qs}` });
  },
  logById: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/${id}` }),
  correlation: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/correlation/${enc(id)}` }),
  stats: (since?: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/stats${since ? "?since=" + enc(since) : ""}` }),
  timeline: (hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/stats/timeline?hours=${hours}` }),
  filters: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/filters` }),
  sessionEvents: (sessionId: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs?sessionId=${enc(sessionId)}&size=100` }),
  trace: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/trace/${enc(id)}` }),
  traceGraph: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/trace/${enc(id)}/graph` }),
  oboReceipt: (correlationId: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/correlation/${enc(correlationId)}/obo-receipt` }),
  activities: (agentKey: string, page: number = 0, size: number = 25) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/activities?agentKey=${enc(agentKey)}&page=${page}&size=${size}` }),
  activitySummary: (agentKey: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/agents/${enc(agentKey)}/activity-summary` }),
  graph: (hours?: number | string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/graph${hours !== null && hours !== undefined && hours !== "" ? `?hours=${hours}` : ""}` }),
};

export const GatewayAgentsApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}` }),
  sessions: (id: string, params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/sessions${qs ? `?${qs}` : ""}` });
  },
  sessionFilters: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/sessions/filters` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/agents/${id}/approve` }),
  block: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/agents/${id}/block` }),
  deprovision: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/agents/${id}/deprovision` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/analytics?hours=${hours}` }),
  liveSessions: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/sessions/live` }),
  sessionTimeline: (sid: string, page: number = 0, size: number = 50) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/sessions/${sid}/timeline?page=${page}&size=${size}` }),
};

export const GatewayPoliciesApi = {
  list: (agentId?: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies${agentId ? `?agentId=${enc(agentId)}` : ""}` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/policies/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/policies/${id}` }),
  toggle: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/${id}/toggle` }),
  reload: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/reload` }),
  stats: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies/stats` }),
  validate: (text: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/validate`, data: { policyText: text } }),
  check: (text: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/check`, data: { policyText: text } }),
  test: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/test`, data: body }),
  chat: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/chat`, data: typeof data === "string" ? { prompt: data } : data }),
  chatSave: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/chat/save`, data: typeof data === "string" ? { prompt: data } : data }),
};

export const GatewayProfilesApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles` }),
  templates: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/templates` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/capability-profiles/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/capability-profiles/${id}` }),
  assign: (profileId: string, agentId: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles/${profileId}/assign/${agentId}` }),
  unassign: (profileId: string, agentId: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/capability-profiles/${profileId}/assign/${agentId}` }),
  preview: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/${id}/preview` }),
  agentAccess: (agentId: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/agent-access/${agentId}` }),
  availableServers: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/available-servers` }),
  chat: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles/chat`, data: typeof data === "string" ? { prompt: data } : data }),
  chatStatus: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/chat/status` }),
};

export const GatewayCustomAttributesApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/custom-attributes`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/custom-attributes/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/custom-attributes/${id}` }),
  toggle: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/custom-attributes/${id}/toggle` }),
  stats: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/stats` }),
  sources: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/sources` }),
};

export const GatewayHumanUsersApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}` }),
  sessions: (id: string, params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/sessions${qs ? `?${qs}` : ""}` });
  },
  sessionFilters: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/sessions/filters` }),
  agents: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/agents` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/approve` }),
  block: (id: string, body?: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/block`, data: body }),
  unblock: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/unblock` }),
  search: (q: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/search?q=${enc(q)}` }),
  lineage: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/lineage` }),
  activeNow: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/active-now` }),
  riskAssessment: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/risk-assessment?hours=${hours}` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/analytics?hours=${hours}` }),
};

export const GatewayNhisApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}` }),
  sessions: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/sessions` }),
  agents: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/agents` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/approve` }),
  block: (id: string, body?: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/block`, data: body }),
  unblock: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/unblock` }),
  search: (q: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/search?q=${enc(q)}` }),
  lineage: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/lineage` }),
  activeNow: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/active-now` }),
  riskAssessment: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/risk-assessment?hours=${hours}` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/analytics?hours=${hours}` }),
};

// Gateway STS signing keys (OBO) + session/token revocations. Mirrors OG `sts`.
export const GatewayStsApi = {
  keys: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/sts/keys` }),
  rotate: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/sts/keys/rotate` }),
  rotationPolicy: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/sts/rotation-policy` }),
  setRotationPolicy: (body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/sts/rotation-policy`, data: body }),
  revoke: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/sts/revocations`, data: body }),
  revocations: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/sts/revocations` }),
  revokeSession: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/sts/revocations/session`, data: body }),
  sessionRevocations: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/sts/revocations/session` }),
};

export const GatewayCisoApi = {
  complianceMetrics: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/compliance/metrics` }),
  // Multi-framework evidence packs (soc2 | sox) — replaced the fixed /compliance/soc2 endpoints.
  frameworks: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/compliance/frameworks` }),
  report: (framework: string) => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/compliance/report/${enc(framework)}` }),
  reportCsv: (framework: string) => gw<string>({ method: "GET", url: `${BASE}/api/admin/ciso/compliance/report/${enc(framework)}`, params: { format: "csv" } }),

  // Executive posture dashboard — every widget is backed by real governed-traffic data (/api/admin/ciso/dashboard/*).
  dashboard: {
    overview: (window?: string) =>
      gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/overview`, params: { window: window || "24h" } }),
    priorityActions: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/priority-actions` }),
    traffic: (window?: string) =>
      gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/traffic`, params: { window: window || "24h" } }),
    hotspots: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/hotspots` }),
    chains: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/chains` }),
    topTools: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/ciso/dashboard/top-tools` }),
  },
};

// Serialize array params as repeated keys (e.g. sensitivity=A&sensitivity=B), matching the backend
// contract. Axios' default serializer would emit sensitivity[]=A, which the controller doesn't bind.
function ppQuery(params?: Record<string, any>): string {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      v.forEach((item) => { if (item !== null && item !== undefined && item !== "") qs.append(k, String(item)); });
    } else if (v !== null && v !== undefined && v !== "") {
      qs.append(k, String(v));
    }
  });
  const q = qs.toString();
  return q ? `?${q}` : "";
}

export const GatewayPostProcessorApi = {
  // Classifications (egress forensics)
  classifications: (params?: Record<string, any>) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/classifications${ppQuery(params)}` }),
  classificationFilters: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/classifications/filters` }),
  classificationSummary: (params?: Record<string, any>) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/classifications/summary${ppQuery(params)}` }),
  reprocess: (correlationId: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/classifications/reprocess`, params: { correlationId } }),
  reprocessBatch: (correlationIds: string[]) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/classifications/reprocess-batch`, data: correlationIds }),
  reprocessAll: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/classifications/reprocess-all` }),
  reprocessTrace: (traceId: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/classifications/reprocess-trace`, params: { traceId } }),

  // Insights
  insights: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/insights` }),
  entitySensitivity: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/insights/entity-sensitivity` }),

  // Built-in detectors (#52)
  detectorsEffective: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/detectors/effective` }),
  detectorEnabled: (matcher: string, enabled: boolean) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/detectors/${enc(matcher)}/enabled`, data: { enabled } }),
  detectorOverride: (matcher: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/post-processor/detectors/${enc(matcher)}/override`, data: body }),
  detectorClearOverride: (matcher: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/post-processor/detectors/${enc(matcher)}/override` }),

  // Custom rules
  rules: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/rules` }),
  createRule: (body: any) => gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/rules`, data: body }),
  updateRule: (id: string, body: any) => gw<any>({ method: "PUT", url: `${BASE}/api/admin/post-processor/rules/${enc(id)}`, data: body }),
  toggleRule: (id: string) => gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/rules/${enc(id)}/toggle` }),
  deleteRule: (id: string) => gw<any>({ method: "DELETE", url: `${BASE}/api/admin/post-processor/rules/${enc(id)}` }),

  // AI rule assistant
  ruleChat: (body: any) => gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/rules/chat`, data: body }),
  ruleChatStatus: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/rules/chat/status` }),
  ruleChatSuggestions: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/rules/chat/suggestions` }),

  // Rule template library (#63)
  templates: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/post-processor/rules/templates` }),
  installTemplate: (templateId: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/rules/templates/${enc(templateId)}/install` }),
  installPack: (industry: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/post-processor/rules/templates/packs/install`, params: { industry } }),
};

export const GatewayA2AApi = {
  list: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/a2a/agents` }),
  get: (name: string) => gw<any>({ method: "GET", url: `${BASE}/api/admin/a2a/agents/${enc(name)}` }),
  register: (body: any) => gw<any>({ method: "POST", url: `${BASE}/api/admin/a2a/agents`, data: body }),
  // Bulk register from Agent Card JSON — accepts a single card (object) or an array of cards.
  importCards: (body: any) => gw<any>({ method: "POST", url: `${BASE}/api/admin/a2a/agents/import`, data: body }),
  remove: (name: string) => gw<any>({ method: "DELETE", url: `${BASE}/api/admin/a2a/agents/${enc(name)}` }),
  skills: () => gw<any>({ method: "GET", url: `${BASE}/api/admin/a2a/skills` }),
  health: (name: string) => gw<any>({ method: "GET", url: `${BASE}/api/admin/a2a/agents/${enc(name)}/health` }),
};
